import * as d3 from 'd3';
import d3Tip from 'd3-tip';
import {TFunction} from 'i18next';
import {generatePath} from 'react-router-dom';

import {DocumentType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {colors} from 'theme';
import {ProductOperationsRawRow, ProductTracing} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';

const convertDocumentNumberLink = (elem: ProductOperationsRawRow) => {
    if (elem.source === 'CUSTOMS') {
        const isCustomsRole = isCustomsRole$.getState();
        return handleNil(elem.reportId, (id) => {
            if (isCustomsRole) {
                return id;
            }
            return generatePath(ROUTE.DOCUMENTS_GDS_DETAIL, {id});
        });
    }

    if (elem.operationAction === DocumentType.GENERATED)
        return handleNil(elem.orderId, (value) => generatePath(ROUTE.ORDERS_DETAIL, {id: value}));

    if (elem.operationAction === DocumentType.SOLD)
        return handleNil(elem.reportId, (value) =>
            generatePath(ROUTE.RECEIPTS_DETAIL, {id: value}),
        );

    return handleNil(elem.reportId, (id) => {
        if (elem.operationAction === DocumentType.SALE_BY_INVOICE)
            return generatePath(ROUTE.INVOICES_DETAIL, {id});
        if (![DocumentType.ISSUED].includes(elem.operationAction))
            return generatePath(ROUTE.DOCUMENTS_DETAIL, {id});

        return null;
    });
};

const convertDocumentNumber = (elem: ProductOperationsRawRow) => {
    if (elem.operationAction === DocumentType.GENERATED) {
        return handleNil(elem.orderId);
    }

    return handleNil(elem.reportId, (value) =>
        elem.operationAction !== DocumentType.ISSUED ? value : null,
    );
};

type CustomSelection<T> = d3.Selection<SVGGElement, T, SVGGElement, unknown>;
type Statement<T extends {}> = {
    key: keyof T;
    type: 'label-value' | 'link';
    transform?: (value: T) => any;
    label: (value: T) => string;
    tip?: boolean;
    showIf?: (value: T) => boolean;
};

const config = {
    initialZoom: 0.85,
    width: 1272,
    height: 223,
    margin: {
        left: 30,
        top: 0,
        bottom: 0,
        right: 30,
    },
    card: {
        width: 419,
        height: 136,
        px: 24,
        py: 16,
        spaceBetween: 42,
        spaceInside: 32,
        icon: {
            size: 48,
        },
    },
};

const tracingStatements: Statement<ProductTracing>[] = [
    // {
    //     key: 'updDate',
    //     label: 'pages.products.detail.tracing.date',
    //     type: 'label-value',
    //     transform: (value) => handleNil(value.updDate, (value) => formatDate(value.toString())),
    // },
    {
        key: 'price',
        label: () => 'pages.products.detail.tracing.price',
        type: 'label-value',
        transform: (value) => handleNil(value.price, formatNumber),
        showIf: (value) => +value.price > 0,
    },
];

const operationsStatements: Statement<ProductOperationsRawRow>[] = [
    {
        key: 'clientName',
        label: () => '',
        type: 'label-value',
        transform: (value) => handleNil(value.clientName),
        tip: true,
    },
    {
        key: 'operationAction',
        label: () => 'pages.products.detail.tracing.doc-link',
        type: 'label-value',
        transform: () => '',
        showIf: (value) => convertDocumentNumberLink(value) !== null,
    },
    {
        key: 'operationAction',
        label: convertDocumentNumber,
        type: 'link',
        transform: (value) => convertDocumentNumberLink(value),
    },
];

export function build(
    data: {tracing: ProductTracing[]; operations: ProductOperationsRawRow[]},
    t: TFunction,
) {
    const operationsData = data.operations;
    // .filter((d) => d.operationAction !== ProductsStatus.SOLD);
    const operationsCardCount = operationsData.length;
    const tracingCardCount = data.tracing.length;
    const totalCardCount = tracingCardCount + operationsCardCount;
    const svg = d3
        .select('#trace-line')
        .attr('width', '100%')
        .attr('height', config.height + 'px')
        .attr('viewBox', [0, 0, config.width, config.height].join(' '))
        .style('font', '10px sans-serif')
        .style('flex', '1 1 auto')
        .style('user-select', 'none')
        .attr('cursor', 'grab');

    addLine();
    const gCard = svg.append('g');

    const tracingTip = (d3Tip as any)()
        .attr('class', 'tl-name-tip')
        .style('z-index', 10000)
        .offset([-10, 0])
        .html((_: any, d: ProductTracing) =>
            d.inn === '0' ? (t('pages.products.detail.tracing.sold') as string) : d.name,
        );

    const operationTip = (d3Tip as any)()
        .attr('class', 'tl-name-tip')
        .style('z-index', 10000)
        .offset([-10, 0])
        .html((_: any, d: ProductOperationsRawRow) => d.clientName);

    svg.call(tracingTip);
    svg.call(operationTip);

    applyZoom();

    buildOperations(operationsData);
    buildTracing(data.tracing);

    function buildOperations(operationsData: ProductOperationsRawRow[]) {
        const cards = gCard.selectAll('g.operation').data(operationsData, (_, index) => index);

        const cardsEnter = cards
            .enter()
            .append('g')
            .classed('operation', true)
            .attr(
                'transform',
                (_, i) =>
                    `translate(${config.margin.left +
                        (config.card.width + config.card.spaceBetween) * i},${config.height / 2 -
                        config.card.height / 2})`,
            );

        addBackground(cardsEnter);
        addIcon(cardsEnter, (d) => `/icons/tracing/${d.operationAction}.svg`);
        addContent(
            cardsEnter,
            operationsStatements,
            (d) => t(`pages.products.detail.operations.operationType.${d.operationAction}`),
            false,
        );
        addDate(cardsEnter, (d) => formatDate(d.operationTime, 'dd MMMM yyyy'));
    }

    function buildTracing(tracingData: ProductTracing[]) {
        const cards = gCard.selectAll('g.tracing').data(tracingData, (_, index) => index);

        const cardsEnter = cards
            .enter()
            .append('g')
            .classed('tracing', true)

            .attr(
                'transform',
                (_, i) =>
                    `translate(${config.margin.left +
                        (config.card.width + config.card.spaceBetween) *
                            (i + operationsCardCount)},${config.height / 2 -
                        config.card.height / 2})`,
            );

        addBackground(cardsEnter);
        addIcon(cardsEnter, (d) => `/icons/tracing/${d.inn === '0' ? 'SOLD' : 'transport'}.svg`);
        addContent(
            cardsEnter,
            tracingStatements,
            (d) => (d.inn === '0' ? (t('pages.products.detail.tracing.sold') as string) : d.name),
            true,
        );
        addDate(cardsEnter, (d) => formatDate(d.updDate.toString(), 'dd MMMM yyyy'));
    }

    function addDate<T>(selection: CustomSelection<T>, getDate: (d: T) => string) {
        selection
            .append('text')
            .attr('transform', `translate(0, -18)`)
            .classed('tl-content tl-statement', true)
            .attr('fill', colors.textSecondary)
            .text(getDate);
    }

    function addLine() {
        svg.append('line')
            .attr('stroke', '#E1E8EF')
            .attr('stroke-width', 1)
            .attr('y1', config.height / 2)
            .attr('x1', config.margin.left + config.card.width / 2)
            .attr('y2', config.height / 2)
            .attr(
                'x2',
                config.margin.left +
                    config.card.width * totalCardCount +
                    config.card.spaceBetween * (totalCardCount - 1),
            );
    }

    function addContent<T>(
        selection: CustomSelection<T>,
        statements: Statement<T>[],
        getName: (d: T) => string,
        nameTip: boolean,
    ) {
        const wrapper = selection
            .append('foreignObject')
            .classed('tl-content', true)
            .attr('x', config.card.px + config.card.icon.size + config.card.spaceInside)
            .attr('y', config.card.py)
            .attr(
                'width',
                config.card.width -
                    (config.card.px * 2 + config.card.icon.size + config.card.spaceInside),
            )
            .attr('height', config.card.height - config.card.py * 2);
        const name = wrapper
            .append('xhtml:span')
            .classed('tl-name', true)
            .style('color', colors.textPrimary)
            .text(getName);

        if (nameTip) {
            name.on('mouseover', tracingTip.show).on('mouseout', tracingTip.hide);
        }

        const isCustomsRole = isCustomsRole$.getState();
        statements.forEach((statement) => {
            const transform = (d: T) =>
                statement.transform ? statement.transform(d) : d[statement.key];
            const g = wrapper
                .append('xhtml:div')
                .classed('tg-statement', true)
                .style('color', colors.textSecondary);
            if (statement.type === 'label-value') {
                g.append('xhtml:span')
                    .filter((d) => (statement.showIf ? statement.showIf(d) : true))
                    .text((d) => t(statement.label(d)) + ' ');
                const value = g
                    .append('xhtml:span')
                    .filter((d) => (statement.showIf ? statement.showIf(d) : true))
                    .style('pointer-events', statement.tip ? 'all' : 'none')
                    .text(transform);
                if (statement.tip) {
                    value.on('mouseover', operationTip.show).on('mouseout', operationTip.hide);
                }
            }
            if (statement.type === 'link') {
                if (isCustomsRole) {
                    g.append('xhtml:span')
                        .filter((d) => (statement.showIf ? statement.showIf(d) : true))
                        .filter((d) => !!transform(d))
                        .style('pointer-events', 'none')
                        .style('color', colors.textSecondary)
                        .text((d) =>
                            t(statement.label(d))
                                ? (t(statement.label(d)) as string)
                                : transform(d),
                        );
                } else {
                    g.append('xhtml:a')
                        .filter((d) => (statement.showIf ? statement.showIf(d) : true))
                        .filter((d) => !!transform(d))
                        .style('pointer-events', 'all')
                        .style('color', colors.brandPrimary)
                        .attr('href', transform)
                        .text((d) =>
                            t(statement.label(d))
                                ? (t(statement.label(d)) as string)
                                : transform(d),
                        );
                }
            }
        });
    }

    function addIcon<T>(selection: CustomSelection<T>, getHref: (d: T) => string) {
        const g = selection
            .append('g')
            .classed('tl-icon', true)
            .attr('cursor', 'default')
            .attr(
                'transform',
                `translate(${config.card.px}, ${config.card.height / 2 -
                    config.card.icon.size / 2})`,
            );

        g.append('rect')
            .attr('width', config.card.icon.size)
            .attr('height', config.card.icon.size);

        g.append('image')
            .attr(
                'transform',
                `translate(${config.card.icon.size / 4}, ${config.card.icon.size / 4})`,
            )
            .attr('width', config.card.icon.size / 2)
            .attr('height', config.card.icon.size / 2)
            .attr('href', getHref);
    }

    function addBackground(selection: CustomSelection<any>) {
        selection
            .append('rect')
            .classed('tl-background', true)
            .attr('width', config.card.width)
            .attr('height', config.card.height);
    }

    function applyZoom() {
        function handleZoom(e: d3.D3ZoomEvent<any, any>) {
            const {x} = e.transform;
            const value = Math.max(
                Math.min(x, 0),
                -totalCardCount * config.card.width -
                    (totalCardCount - 1) * config.card.spaceBetween,
            );
            svg.attr('cursor', 'grabbing');
            svg.selectChildren('*').attr(
                'transform',
                `translate(${value}, ${(config.height / 2 - config.card.height * 0.675) *
                    config.initialZoom}) scale(${config.initialZoom})`,
            );
        }

        function disableZoom() {
            svg.attr('cursor', 'grab');
        }

        const zoom = d3
            .zoom()
            .scaleExtent([0.1, 2])
            .on('zoom', handleZoom)
            .on('end', disableZoom) as any;

        svg.call(zoom).on('dblclick.zoom', null);
        svg.call(zoom.transform, d3.zoomIdentity.scale(config.initialZoom));
    }
}
